import { useCallback, useEffect, useState } from 'react';
import {
  Check,
  CreditCard,
  Globe,
  Mail,
  Receipt,
  Save,
  Settings as SettingsIcon,
  Wallet,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import type { ApiError } from '../../lib/api';
import {
  adminApi,
  type AdminSettingField,
  type AdminSettingGroup,
  type AdminSettings,
} from '../../lib/admin';

const GROUP_ICON: Record<string, typeof Wallet> = {
  payments_qr: Wallet,
  payments_card: CreditCard,
  email: Mail,
  seller: Receipt,
  web: Globe,
};

const inputClass =
  'mt-1 h-11 w-full rounded-xl border border-line bg-paper px-3 text-ink outline-none focus:border-board';

/** Lokální rozpracované hodnoty formuláře. */
type Draft = {
  /** Nesecret hodnoty (text/number/bool) klíčované podle klíče pole. */
  values: Record<string, string | number | boolean>;
  /** Nové hodnoty tajemství (prázdné = ponechat beze změny). */
  secrets: Record<string, string>;
  /** Klíče tajemství určené k vymazání. */
  clear: Record<string, boolean>;
};

function buildDraft(settings: AdminSettings): Draft {
  const values: Record<string, string | number | boolean> = {};
  const secrets: Record<string, string> = {};
  const clear: Record<string, boolean> = {};
  for (const group of settings.groups) {
    for (const field of group.fields) {
      if (field.is_secret) {
        secrets[field.key] = '';
        clear[field.key] = false;
      } else if (field.type === 'bool') {
        values[field.key] = Boolean(field.value);
      } else if (field.type === 'number') {
        values[field.key] = typeof field.value === 'number' ? field.value : 0;
      } else {
        values[field.key] = field.value == null ? '' : String(field.value);
      }
    }
  }
  return { values, secrets, clear };
}

/* Pole */

function BoolField({
  field,
  checked,
  onChange,
}: {
  field: AdminSettingField;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-line bg-paper px-3 py-2.5">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink">{field.label}</p>
        <p className="mt-0.5 text-xs text-ink-soft">{field.help}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={field.label}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 h-6 w-11 flex-none rounded-full transition ${
          checked ? 'bg-board' : 'bg-line'
        }`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-card shadow transition-all ${
            checked ? 'left-[22px]' : 'left-0.5'
          }`}
        />
      </button>
    </div>
  );
}

function SecretField({
  field,
  input,
  clear,
  onInput,
  onClear,
}: {
  field: AdminSettingField;
  input: string;
  clear: boolean;
  onInput: (v: string) => void;
  onClear: (v: boolean) => void;
}) {
  return (
    <label className="block">
      <span className="flex items-center gap-2 text-sm font-semibold text-ink">
        {field.label}
        {field.is_set ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-xs font-semibold text-success">
            <Check className="h-3 w-3" strokeWidth={2.5} /> Nastaveno
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-xs font-semibold text-warning">
            <X className="h-3 w-3" strokeWidth={2.5} /> Nenastaveno
          </span>
        )}
      </span>
      <input
        type="password"
        autoComplete="new-password"
        value={input}
        disabled={clear}
        onChange={(e) => onInput(e.target.value)}
        placeholder={field.is_set ? 'ponechat beze změny' : 'zatím nenastaveno'}
        className={`${inputClass} disabled:opacity-50`}
      />
      <span className="mt-0.5 block text-xs text-ink-soft">{field.help}</span>
      {field.is_set && (
        <label className="mt-1.5 inline-flex items-center gap-2 text-xs font-medium text-ink-soft">
          <input
            type="checkbox"
            checked={clear}
            onChange={(e) => onClear(e.target.checked)}
            className="h-4 w-4 rounded border-line"
          />
          Vymazat stávající hodnotu
        </label>
      )}
    </label>
  );
}

function PlainField({
  field,
  value,
  onChange,
}: {
  field: AdminSettingField;
  value: string | number;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-ink">{field.label}</span>
      <input
        type={field.type === 'number' ? 'number' : 'text'}
        value={value}
        inputMode={field.type === 'number' ? 'numeric' : undefined}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
      />
      <span className="mt-0.5 block text-xs text-ink-soft">{field.help}</span>
    </label>
  );
}

function GroupCard({
  group,
  draft,
  set,
}: {
  group: AdminSettingGroup;
  draft: Draft;
  set: (updater: (d: Draft) => Draft) => void;
}) {
  const Icon = GROUP_ICON[group.key] ?? SettingsIcon;
  return (
    <section className="rounded-card border border-line bg-card p-5 shadow-card">
      <header className="mb-3 flex items-center gap-2.5">
        <span className="grid h-9 w-9 place-items-center rounded-[11px] bg-board/10">
          <Icon className="h-5 w-5 text-board" strokeWidth={2} />
        </span>
        <div>
          <h2 className="font-serif text-lg leading-tight text-ink">{group.title}</h2>
          <p className="text-xs text-ink-soft">{group.help}</p>
        </div>
      </header>
      <div className="space-y-3">
        {group.fields.map((field) => {
          if (field.is_secret) {
            return (
              <SecretField
                key={field.key}
                field={field}
                input={draft.secrets[field.key] ?? ''}
                clear={draft.clear[field.key] ?? false}
                onInput={(v) => set((d) => ({ ...d, secrets: { ...d.secrets, [field.key]: v } }))}
                onClear={(v) => set((d) => ({ ...d, clear: { ...d.clear, [field.key]: v } }))}
              />
            );
          }
          if (field.type === 'bool') {
            return (
              <BoolField
                key={field.key}
                field={field}
                checked={Boolean(draft.values[field.key])}
                onChange={(v) => set((d) => ({ ...d, values: { ...d.values, [field.key]: v } }))}
              />
            );
          }
          return (
            <PlainField
              key={field.key}
              field={field}
              value={draft.values[field.key] as string | number}
              onChange={(v) =>
                set((d) => ({
                  ...d,
                  values: {
                    ...d.values,
                    [field.key]: field.type === 'number' ? Number(v) : v,
                  },
                }))
              }
            />
          );
        })}
      </div>
    </section>
  );
}

/* Stránka */

export default function AdminSettingsPage() {
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getSettings();
      setSettings(data);
      setDraft(buildDraft(data));
    } catch (err) {
      toast.error('Nastavení se nepodařilo načíst', {
        description: (err as ApiError)?.message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const updateDraft = useCallback((updater: (d: Draft) => Draft) => {
    setDraft((prev) => (prev ? updater(prev) : prev));
  }, []);

  const save = async () => {
    if (!settings || !draft) return;
    setSaving(true);
    try {
      const values: Record<string, string | number | boolean> = { ...draft.values };
      const clear_secrets: string[] = [];
      for (const group of settings.groups) {
        for (const field of group.fields) {
          if (!field.is_secret) continue;
          const secretInput = draft.secrets[field.key] ?? '';
          if (draft.clear[field.key]) clear_secrets.push(field.key);
          else if (secretInput) values[field.key] = secretInput;
        }
      }
      const updated = await adminApi.updateSettings({ values, clear_secrets });
      setSettings(updated);
      setDraft(buildDraft(updated));
      toast.success('Nastavení uloženo');
    } catch (err) {
      toast.error('Uložení se nepodařilo', { description: (err as ApiError)?.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl">Nastavení</h1>
          <p className="mt-0.5 text-sm text-ink-soft">
            Provozní údaje e-shopu, platby, e-maily a údaje na doklad.
          </p>
        </div>
      </div>

      {loading || !settings || !draft ? (
        <div className="space-y-4">
          <div className="h-48 rounded-card bg-paper2 motion-safe:animate-pulse" />
          <div className="h-48 rounded-card bg-paper2 motion-safe:animate-pulse" />
        </div>
      ) : (
        <div className="space-y-4">
          {settings.groups.map((group) => (
            <GroupCard key={group.key} group={group} draft={draft} set={updateDraft} />
          ))}

          <div className="sticky bottom-3 flex justify-end">
            <button
              type="button"
              disabled={saving}
              onClick={() => void save()}
              className="inline-flex h-12 items-center gap-2 rounded-xl bg-board px-6 text-sm font-semibold text-chalk shadow-card transition hover:bg-board-deep disabled:opacity-50"
            >
              <Save className="h-4 w-4" strokeWidth={2} />
              {saving ? 'Ukládám…' : 'Uložit nastavení'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
